/**
 * A meeting, in its four states: joining, waiting at the door, live, and over.
 *
 * ── The stage and the bar are the call's, not a second copy ─────────────────
 * A call and a meeting are two concepts and one medium. `CallOverlay` is the
 * tile grid, the control bar and the keyboard, and everything this file adds
 * sits around it: the lobby, the host controls, the policy, the recording
 * banner, the board. Rebuilding the grid here would be two grids to keep in
 * step and one of them would lose the speaking outline.
 *
 * ── The board opens in the room ─────────────────────────────────────────────
 * Over the stage rather than on its own page, for everybody: `/boards/:id` is
 * behind the sign-in gate a guest cannot pass, and leaving this component
 * leaves the meeting. The call stays mounted with its stage hidden, so its
 * audio plays on and its bar — mute, camera, leave — stays in reach.
 *
 * ── The waiting view has no roster, and that is the feature ─────────────────
 * Somebody who has not been admitted sees the title and their own standing.
 * Not who is in the room, not how many — a party learning the roster from the
 * door is exactly the disclosure the lobby exists to prevent, and the server
 * says the same thing by refusing to answer it.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ds } from '../../ds';
import { useT } from '../../i18n';
import { store, useStore } from '../../lib/relay/store';
import { openCallSession } from '../../lib/calls/session';
import { participantLabel } from '../../lib/relay/types';
import type {
  Admission,
  MeetingParticipant,
  MeetingPolicy,
  MeetingRole
} from '../../lib/relay/types';
import type { RemotePeer } from '../../lib/calls/types';
import { CallOverlay, useCallState } from '../call/CallOverlay';
import { Canvas } from '../board/Canvas';
import { useBoard } from '../board/useBoard';
import { isHost, muteParty, openMeetingBoard, setPolicy, stopShare } from './api';
import { PolicyEditor } from './PolicyEditor';
import { TranscriptPanel } from './TranscriptPanel';
import '../call/call.css';

/** The same empty array every time: `useStore` compares with `Object.is`, so a
 *  fresh one per render would re-render for ever. */
const NO_LOBBY: MeetingParticipant[] = [];

export interface MeetingRoomProps {
  meetingId: string;
  title: string;
  /** This browser's role. Host and cohost see the lobby, the host ops and the
   *  policy; an attendee sees none of them. */
  role: MeetingRole;
  policy?: MeetingPolicy;
  /** Who runs the meeting, for the recording banner when somebody else started
   *  it. */
  hostName?: string;
  /** Where this browser stands. Anything but `admitted` is the door. */
  admission?: Admission;
  /** Set when this browser came in through `/m/:token`: the header credential,
   *  and the name that is drawn with "(Guest)" everywhere. */
  guest?: { token: string; displayName: string };
  ended?: boolean;
  onLeave: () => void;
}

export function MeetingRoom({
  meetingId,
  title,
  role,
  policy,
  hostName,
  admission = 'admitted',
  guest,
  ended,
  onLeave
}: MeetingRoomProps) {
  const { Badge, Button, Icon } = ds();
  const t = useT();

  const session = useMemo(() => openCallSession({ kind: 'meeting', id: meetingId }), [meetingId]);
  const { status, peers, refresh } = useCallState(session);
  const [side, setSide] = useState<'none' | 'people' | 'policy'>('none');
  const [recordingBy, setRecordingBy] = useState<string | null>(null);
  const [boardId, setBoardId] = useState<string | null>(null);
  const [boardOpen, setBoardOpen] = useState(false);
  const host = isHost(role);

  // The lobby is the host's own read, and reading it is a request: `lobby()`
  // asks the server the first time anybody selects it. An attendee is refused
  // with a 403 and a guest — who has no session at all — with a 401, for a list
  // neither of them is ever shown. Only a host asks.
  const lobby = useStore((s) => (host ? s.lobby(meetingId) : NO_LOBBY));

  // `guest` is an object literal built by the caller's render, so it is a new
  // one every time that component renders. Depending on it would tear the media
  // down and rejoin on a re-render nothing to do with the meeting — and the
  // rejoin loses, because `leave()` has not finished settling the session by the
  // time `join()` reads its status and returns early. The token is the identity.
  const guestToken = guest?.token;

  useEffect(() => {
    if (admission !== 'admitted' || ended) return;
    void session.join();
    // A meeting is a call, so presence says so and is put back afterwards. It
    // is also what stops a conversation ringing over the top of one: the ring
    // rules read `busy` as do-not-disturb. A guest has no account and no
    // presence to set.
    const before = guestToken ? null : store.me()?.presence ?? 'online';
    if (before) store.setPresence('busy');
    return () => {
      void session.leave();
      if (before) store.setPresence(before);
    };
  }, [session, admission, ended, guestToken]);

  useEffect(() => {
    return session.on('recordingChanged', (active) => {
      setRecordingBy(active ? (recordingBy ?? hostName ?? t('call.recording.someone')) : null);
      refresh();
    });
  }, [session, hostName, recordingBy, refresh, t]);

  const toggleRecording = useCallback(() => {
    const mine = guest?.displayName ?? store.me()?.name ?? t('call.recording.someone');
    if (session.recording.active) {
      void session.recording.stop().then(() => setRecordingBy(null));
    } else {
      void session.recording.start().then(() => setRecordingBy(mine));
    }
  }, [session, guest, t]);

  const toggleBoard = useCallback(() => {
    if (boardOpen) {
      setBoardOpen(false);
      return;
    }
    if (boardId) {
      setBoardOpen(true);
      return;
    }
    void openMeetingBoard(meetingId, guestToken).then((id) => {
      setBoardId(id);
      setBoardOpen(true);
    });
  }, [boardOpen, boardId, meetingId, guestToken]);

  if (ended) {
    return (
      <div className="mv-call">
        <Header title={title} guest={guest} />
        <div className="mv-call-stage" style={{ alignItems: 'start' }}>
          <div style={{ width: '100%', maxWidth: 720 }}>
            <p style={{ color: 'var(--text-muted)' }}>{t('call.ended.line')}</p>
            <TranscriptPanel meetingId={meetingId} guestToken={guest?.token} />
          </div>
        </div>
      </div>
    );
  }

  if (admission !== 'admitted') {
    return (
      <div className="mv-call">
        <Header title={title} guest={guest} />
        <div className="mv-call-stage">
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)' }}>
              {admission === 'denied' ? t('guest.denied') : t('guest.knocking')}
            </p>
            <div style={{ marginTop: 'var(--space-16)' }}>
              <Button variant="secondary" onClick={onLeave}>
                {t('meetings.room.leave')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'idle' || status === 'joining') {
    return (
      <div className="mv-call">
        <Header title={title} guest={guest} />
        <div className="mv-call-stage">
          <span style={{ color: 'var(--text-muted)' }}>{t('call.state.joining')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mv-call">
      <Header
        title={title}
        guest={guest}
        actions={
          <>
            <Button
              size="sm"
              variant={boardOpen ? 'primary' : 'secondary'}
              onClick={toggleBoard}
            >
              {t('call.board')}
            </Button>
            {host && (
              <Button
                size="sm"
                variant={session.recording.active ? 'danger' : 'secondary'}
                onClick={toggleRecording}
              >
                {session.recording.active ? t('call.recording.stop') : t('call.recording.start')}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSide(side === 'people' ? 'none' : 'people')}
            >
              {t('call.people')}
            </Button>
            {host && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSide(side === 'policy' ? 'none' : 'policy')}
              >
                {t('call.policy.title')}
              </Button>
            )}
          </>
        }
      />

      {(recordingBy || session.recording.active) && (
        <div className="mv-call-banner" role="status">
          <Icon name="Circle" size={14} />
          {t('call.recording.banner', { name: recordingBy ?? hostName ?? '' })}
        </div>
      )}

      {boardOpen && boardId && (
        <RoomBoard boardId={boardId} name={title} {...(guestToken ? { guestToken } : {})} />
      )}
      <div className={boardOpen ? 'mv-call-boarded' : undefined} style={{ display: 'contents' }}>
        <CallOverlay
          session={session}
          direction="outgoing"
          peerName={title}
          onHangUp={onLeave}
        />
      </div>

      {host && lobby.length > 0 && (
        <div className="mv-call-lobby">
          <div className="mv-call-menu-label">{t('meetings.lobby.title')}</div>
          {lobby.map((party) => (
            <div key={party.id} className="mv-call-lobby-row">
              <span style={{ flex: 1, minWidth: 0 }}>{participantLabel(party)}</span>
              <Button size="sm" onClick={() => void store.admit(meetingId, party.id)}>
                {t('meetings.lobby.admit')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void store.deny(meetingId, party.id)}
              >
                {t('meetings.lobby.deny')}
              </Button>
            </div>
          ))}
        </div>
      )}

      {side === 'people' && (
        <div className="mv-call-side">
          <div className="mv-call-menu-label">{t('call.people')}</div>
          {peers.length === 0 ? (
            <span style={{ color: 'var(--text-muted)' }}>{t('meetings.room.alone')}</span>
          ) : (
            peers.map((peer) => (
              <PersonRow
                key={peer.participantId}
                peer={peer}
                meetingId={meetingId}
                canHost={host}
              />
            ))
          )}
        </div>
      )}

      {side === 'policy' && host && policy && (
        <div className="mv-call-side">
          <PolicyEditor
            policy={policy}
            participants={peers.map(asParticipant)}
            onChange={(next) => void setPolicy(meetingId, next)}
          />
        </div>
      )}

      {side === 'policy' && host && !policy && (
        <div className="mv-call-side">
          <Badge tone="neutral">{t('call.policy.unavailable')}</Badge>
        </div>
      )}
    </div>
  );
}

/** The meeting's board, in place of the stage. */
function RoomBoard({
  boardId,
  guestToken,
  name
}: {
  boardId: string;
  guestToken?: string;
  name: string;
}) {
  const { Badge } = ds();
  const t = useT();
  const { elements, status, problem, others, apply, cursor, live } = useBoard(boardId, guestToken);

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
      <Canvas
        elements={elements}
        apply={apply}
        name={name}
        others={others}
        onCursor={cursor}
        onLive={live}
      />
      {status !== 'open' && (
        <div style={{ position: 'absolute', right: 'var(--space-12)', bottom: 'var(--space-12)' }}>
          <Badge tone={status === 'error' ? 'danger' : 'neutral'} dot>
            {status === 'error' ? problem || t('boards.trouble') : t('boards.connecting')}
          </Badge>
        </div>
      )}
    </div>
  );
}

/** The room's own header. A guest's name sits in it, drawn as a guest's. */
function Header({
  title,
  guest,
  actions
}: {
  title: string;
  guest?: { displayName: string };
  actions?: ReactNode;
}) {
  return (
    <header
      className="mv-strip"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-12)',
        padding: 'var(--space-12) var(--space-16)'
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-display), sans-serif',
            fontWeight: 600,
            fontSize: 18,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {title}
        </div>
        {guest && (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {participantLabel({ displayName: guest.displayName, guest: true })}
          </div>
        )}
      </div>
      {/* Wrapping, like the call bar and the board's toolbar. Four buttons with
          words on them do not fit beside a title at 375px, and a header that
          overflows takes the whole page sideways with it. */}
      {actions && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: 'var(--space-8)'
          }}
        >
          {actions}
        </div>
      )}
    </header>
  );
}

/** One seat, and what a host may do to it. Never the host's own seat: the
 *  server refuses a host op on a host, and a button that cannot work is worse
 *  than one that is absent. */
function PersonRow({
  peer,
  meetingId,
  canHost
}: {
  peer: RemotePeer;
  meetingId: string;
  canHost: boolean;
}) {
  const { Button, Icon } = ds();
  const t = useT();
  const target = canHost && peer.role !== 'host';

  return (
    <div className="mv-call-lobby-row">
      <span style={{ flex: 1, minWidth: 0 }}>
        {participantLabel({ displayName: peer.displayName, guest: peer.guest })}
      </span>
      {peer.muted && <Icon name="MicOff" size={14} />}
      {target && !peer.muted && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void muteParty(meetingId, peer.participantId)}
        >
          {t('call.host.mute')}
        </Button>
      )}
      {target && peer.tracks.screen && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void stopShare(meetingId, peer.participantId)}
        >
          {t('call.host.stopshare')}
        </Button>
      )}
    </div>
  );
}

/** A seat as the policy editor reads one. The two shapes are the same roster
 *  row from two routes — `RemotePeer` is the SFU's and `MeetingParticipant` is
 *  the meeting's. */
function asParticipant(peer: RemotePeer): MeetingParticipant {
  return {
    id: peer.participantId,
    displayName: peer.displayName,
    guest: peer.guest,
    role: peer.role,
    muted: peer.muted,
    admission: 'admitted'
  };
}
